/**
 * Chat timestamps from the API are Japan Standard Time as "yyyy-MM-dd HH:mm:ss".
 */

import { JST_TIME_ZONE } from './japanTime';

export type ChatDayKind = 'today' | 'yesterday' | 'other';

function jstYmdOffset(dayOffset: number): string {
  const d = new Date(Date.now() + dayOffset * 24 * 60 * 60 * 1000);
  return d.toLocaleDateString('en-CA', { timeZone: JST_TIME_ZONE });
}

/** Calendar date key yyyy-MM-dd, or empty if unparseable. */
export function chatDateKey(occurredAt: string | null | undefined): string {
  if (!occurredAt) return '';
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(occurredAt.trim());
  return m ? m[1] : '';
}

export function chatTimeLabel(occurredAt: string | null | undefined): string {
  if (!occurredAt) return '';
  const m = /^\d{4}-\d{2}-\d{2}[ T](\d{2}):(\d{2})/.exec(occurredAt.trim());
  return m ? `${m[1]}:${m[2]}` : '';
}

export function chatDayKind(occurredAt: string | null | undefined): ChatDayKind {
  const key = chatDateKey(occurredAt);
  if (!key) return 'other';
  if (key === jstYmdOffset(0)) return 'today';
  if (key === jstYmdOffset(-1)) return 'yesterday';
  return 'other';
}

export function formatChatDate(
  occurredAt: string | null | undefined,
  lang: 'ja' | 'en' | 'vi'
): string {
  const key = chatDateKey(occurredAt);
  if (!key) return occurredAt ?? '';
  const [y, mo, d] = key.split('-').map(Number);
  if (!y || !mo || !d) return key;
  if (lang === 'en') {
    return new Date(y, mo - 1, d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
  if (lang === 'vi') {
    return `${String(d).padStart(2, '0')}/${String(mo).padStart(2, '0')}/${y}`;
  }
  return `${y}年${mo}月${d}日`;
}

export function chatDayLabelJa(occurredAt: string | null | undefined): string {
  const kind = chatDayKind(occurredAt);
  if (kind === 'today') return '今日';
  if (kind === 'yesterday') return '昨日';
  return formatChatDate(occurredAt, 'ja');
}

/** Compact stamp for the admin inbox list (JST). */
export function chatInboxStampJa(occurredAt: string | null | undefined): string {
  if (!occurredAt) return '';
  const kind = chatDayKind(occurredAt);
  if (kind === 'today') return chatTimeLabel(occurredAt);
  if (kind === 'yesterday') return '昨日';
  const key = chatDateKey(occurredAt);
  const parts = key.split('-');
  if (parts.length !== 3) return key;
  return `${Number(parts[1])}/${Number(parts[2])}`;
}
